import { Injectable, signal, effect, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type Theme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  readonly theme = signal<Theme>(this.getInitialTheme());

  readonly isDark = () => this.theme() === 'dark';

  constructor() {
    effect(() => {
      const t = this.theme();
      if (this.isBrowser) {
        document.documentElement.setAttribute('data-theme', t);
        localStorage.setItem('theme', t);
      }
    });
  }

  toggle(): void {
    if (this.isBrowser) {
      const root = document.documentElement;
      root.classList.add('theme-transitioning');
      this.theme.update(t => (t === 'light' ? 'dark' : 'light'));
      setTimeout(() => root.classList.remove('theme-transitioning'), 450);
    } else {
      this.theme.update(t => (t === 'light' ? 'dark' : 'light'));
    }
  }

  private getInitialTheme(): Theme {
    if (!this.isBrowser) return 'light';
    const stored = localStorage.getItem('theme') as Theme | null;
    if (stored) return stored;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
